import {
	Box,
	Card,
	CardContent,
	Checkbox,
	FormControlLabel,
	Grid,
	TextField,
} from "../../../common/MaterialComponent";
import { MultiLanguageWidget } from "../MultiLanguageWidget/MultiLanguageWidget";
import LogRequestForHelpWidgetModel from "../../../models/RequestForHelpTemplates/LogRequestForHelpWidgetModel";
import "./../IVRFlowConfiguration/IVRFlowConfiguration.css";
import { useContext, useEffect, useState } from "react";
import {
	FlowWizardState,
	GetSelectedLanguageList,
	IsAudioConfigurationAllowed,
	IVRFlowWizardContext,
	WidgetPropertyType,
	WizardStepperProp,
} from "../../../hooks/useIVRFlowWidgetConfiguration";
import { WidgetTypeEnum } from "../../../common/enum";
import WidgetLanguageDetailModel from "../../../models/RequestForHelpTemplates/WidgetLanguageDetailModel";
import { MultiAudioUploadWidget } from "../MultiAudioUploadWidget/MultiAudioUploadWidget";
import AudioFileModel from "../../../models/RequestForHelpTemplates/AudioFileModel";
import Shared from "../../../common/shared";
import { useGetComponentStringsContext } from "../../../hooks/useGetComponentStrings";

export type LogRequestPropertyType =
	| "successText"
	| "errorText"
	| "callbackURL"
	| "timeoutCallbackURL";

export type AudioArrayPropertyType = "successAudioFile" | "errorAudioFile";

export const LogRequestForHelpWidget: React.FC<WizardStepperProp> = ({
	ivrFlowType,
	highlightRequiredDetail,
}) => {
	const [
		logRequestForHelpDetail,
		setLogRequestForHelpDetail,
	] = useState<LogRequestForHelpWidgetModel>(
		new LogRequestForHelpWidgetModel()
	);

	const { state, dispatch } = useContext(IVRFlowWizardContext);
	const strings = useGetComponentStringsContext().LogRequestForHelp;

	const updateWidgetDetail = (
		propertyName: string,
		propertyValue: WidgetPropertyType
	) => {
		// dispatch action to update widget detail
		dispatch({
			type: "UPDATE_WIDGET_DETAIL",
			payload: {
				widgetId: logRequestForHelpDetail.id,
				widgetType: logRequestForHelpDetail.type as WidgetTypeEnum,
				propertyName: propertyName,
				propertyValue: propertyValue,
			},
		});
	};

	useEffect(() => {
		const widgetDetail = (state as FlowWizardState).wizardConfig
			.logRequestForHelpWidgetDetail;
		setLogRequestForHelpDetail(widgetDetail!);
		// disabled eslint rule as useEffect needs to be called once on page load
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onMessageValueChange = (
		value: string | WidgetLanguageDetailModel,
		propertyName: LogRequestPropertyType
	) => {
		const logRequestTemp: LogRequestForHelpWidgetModel = {
			...logRequestForHelpDetail,
		};
		if (value instanceof WidgetLanguageDetailModel) {
			const logRequestObj: WidgetLanguageDetailModel[] = logRequestTemp[
				propertyName
			] as WidgetLanguageDetailModel[];

			const index: number = logRequestObj.findIndex(
				(model: WidgetLanguageDetailModel) => model.language === value.language
			);
			if (index > -1) {
				logRequestObj.splice(index, 1, value);
			} else {
				logRequestObj.push(value);
			}
		} else {
			(logRequestTemp[propertyName] as string) = Shared.getSanitizedInputValue(
				value
			);
		}
		setLogRequestForHelpDetail(logRequestTemp);
		updateWidgetDetail(propertyName, logRequestTemp[propertyName]);
	};

	const handleAudioFileChange = (
		audioFileDetail: AudioFileModel,
		property: AudioArrayPropertyType
	) => {
		const logRequestTemp: LogRequestForHelpWidgetModel = {
			...logRequestForHelpDetail,
		};
		const index: number = logRequestTemp[property].findIndex(
			(model: AudioFileModel) => audioFileDetail.language === model.language
		);
		if (index > -1) {
			logRequestTemp[property].splice(index, 1, audioFileDetail);
		} else {
			logRequestTemp[property].push(audioFileDetail);
		}
		setLogRequestForHelpDetail(logRequestTemp);
		updateWidgetDetail(property, logRequestTemp[property]);
	};

	const selectedLanguageList = () => {
		return GetSelectedLanguageList(
			(state as FlowWizardState).wizardConfig.pickLanguageWidgetDetail
		);
	};

	const isLogTimeoutsEnabledHandler = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		const logRequestTemp: LogRequestForHelpWidgetModel = {
			...logRequestForHelpDetail,
		};
		logRequestTemp.isLogTimeoutsEnabled = event.target.checked;
		if (!event.target.checked) {
			logRequestTemp.timeoutCallbackURL = "";
			updateWidgetDetail("timeoutCallbackURL", "");
		}
		setLogRequestForHelpDetail(logRequestTemp);
		updateWidgetDetail("isLogTimeoutsEnabled", event.target.checked);
	};

	return (
		<Card variant="outlined">
			<CardContent>
				<Grid item xs={12}>
					<TextField
						label={strings.callbackURL}
						value={logRequestForHelpDetail.callbackURL}
						onChange={(event) =>
							onMessageValueChange(event.target.value, "callbackURL")
						}
						fullWidth
						multiline
						rows={2}
						required
						error={
							!logRequestForHelpDetail.callbackURL && highlightRequiredDetail
						}
						helperText={
							!logRequestForHelpDetail.callbackURL && highlightRequiredDetail
								? strings.callbackURLErrorMessage
								: ""
						}
					/>
				</Grid>
				<Grid item xs={12}>
					<Box pt={1} pb={1}>
						<FormControlLabel
							control={
								<Checkbox
									color="primary"
									checked={logRequestForHelpDetail.isLogTimeoutsEnabled}
									onChange={(event) => isLogTimeoutsEnabledHandler(event)}
								/>
							}
							label={strings.logTimeouts}
						/>
					</Box>
				</Grid>
				{logRequestForHelpDetail.isLogTimeoutsEnabled && (
					<Grid item xs={12}>
						<TextField
							label={strings.timeoutCallbackURL}
							value={logRequestForHelpDetail.timeoutCallbackURL}
							onChange={(event) =>
								onMessageValueChange(event.target.value, "timeoutCallbackURL")
							}
							fullWidth
							multiline
							rows={2}
							required
							error={
								!logRequestForHelpDetail.timeoutCallbackURL &&
								highlightRequiredDetail
							}
							helperText={
								!logRequestForHelpDetail.timeoutCallbackURL &&
								highlightRequiredDetail
									? strings.timeoutCallbackURLErrorMessage
									: ""
							}
						/>
					</Grid>
				)}
				<MultiLanguageWidget
					title={strings.successTextMessage}
					widgetLanguageDetail={logRequestForHelpDetail.successText}
					selectedLanguageList={selectedLanguageList()}
					onMessageDetailChange={(object: WidgetLanguageDetailModel) =>
						onMessageValueChange(
							Object.assign(new WidgetLanguageDetailModel(), object),
							"successText"
						)
					}
				/>
				{IsAudioConfigurationAllowed(ivrFlowType) && (
					<MultiAudioUploadWidget
						title={strings.successAudioFile}
						widgetLanguageDetail={logRequestForHelpDetail.successAudioFile}
						selectedLanguageList={selectedLanguageList()}
						partitionKey={(state as FlowWizardState).partitionKey}
						rowKey={(state as FlowWizardState).rowKey}
						handleAudioFileDetailChange={(object: AudioFileModel) =>
							handleAudioFileChange(
								object,
								"successAudioFile" as AudioArrayPropertyType
							)
						}
					/>
				)}

				<MultiLanguageWidget
					title={strings.errorTextMessage}
					widgetLanguageDetail={logRequestForHelpDetail.errorText}
					selectedLanguageList={selectedLanguageList()}
					onMessageDetailChange={(object: WidgetLanguageDetailModel) =>
						onMessageValueChange(
							Object.assign(new WidgetLanguageDetailModel(), object),
							"errorText"
						)
					}
				/>
				{IsAudioConfigurationAllowed(ivrFlowType) && (
					<MultiAudioUploadWidget
						title={strings.errorAudioFile}
						widgetLanguageDetail={logRequestForHelpDetail.errorAudioFile}
						selectedLanguageList={selectedLanguageList()}
						partitionKey={(state as FlowWizardState).partitionKey}
						rowKey={(state as FlowWizardState).rowKey}
						handleAudioFileDetailChange={(object: AudioFileModel) =>
							handleAudioFileChange(
								object,
								"errorAudioFile" as AudioArrayPropertyType
							)
						}
					/>
				)}
			</CardContent>
		</Card>
	);
};
